/**
 * "Data sources" — the bottom panel of `/experience` (L1 footer, not a KPI).
 *
 * Three things feed every number above: the **browser extension** posting RUM
 * minutes, the **Cato DEM collector** writing `CatoDEM_CL` every 15 minutes, and
 * the **identity job** rewriting `FoUserMap_CL` daily. This panel is their state
 * and nothing else — last run, last window, rows written, any error, plus "Run
 * now" per runnable task behind a confirmation.
 *
 * ## Why it is here and not on `/dynamics`
 *
 * It was at the *top* of the Dynamics board until 2026-08-21, which put the
 * health of NOVA's own plumbing above the question a business board exists to
 * answer. Its subject is collectors, RUM posts and freshness, which is this
 * board's subject — so it moved here, and it moved to the *bottom*: it explains
 * the numbers above rather than competing with them. The Dynamics slow section
 * carries a link to it, because a quiet console needs this panel to be readable
 * as "calm" rather than "stale".
 *
 * ## Why it exists at all
 *
 * A quiet board is ambiguous. "No slow interaction was attributed to the
 * network" and "the DEM collector has not written a row since Tuesday" render
 * identically on the charts above; "no browser posted" and "browsers post and
 * everything is fast" likewise. This panel is what tells each pair apart, which
 * is why it is a source of truth rather than decoration.
 *
 * Four states for the RUM intake, never two: not configured, disabled, *unknown*
 * (the workspace did not answer the activity query — so nothing is claimed about
 * the fleet, not even "nobody posts"), and reporting with a coverage meter.
 */

import { Icon, Play } from "@norican/icons";
import {
  Alert,
  AlertDialog,
  Button,
  DescriptionList,
  EmptyState,
  Freshness,
  Skeleton,
  StatusStrip,
  Typography,
} from "@norican/react";
import { useState } from "react";

import { RumCoverageMeter } from "@/features/dynamics/console-chips";
import {
  collectTaskCadenceMs,
  collectTaskLabel,
  collectTaskVerdict,
  RUM_STATE_COPY,
  rumRolloutState,
} from "@/features/dynamics/console-format";
import { PanelCard } from "@/features/dynamics/panel-card";
import { formatBinDateTime } from "@/features/dynamics/status";
import {
  type FoConsoleCollectTask,
  type FoConsoleCollectTaskName,
  useFoConsoleCollectStatus,
  useFoConsoleRumStatus,
  useRunFoConsoleCollect,
} from "@/lib/api";

import { formatCount } from "./experience-format";

/** Only these two names are runnable; anything else the backend may add is read-only here. */
function runnableName(name: string): FoConsoleCollectTaskName | null {
  const lower = name.toLowerCase();
  if (lower === "dem" || lower === "identity") {
    return lower;
  }
  return null;
}

export function DataSourcesPanel() {
  return (
    <PanelCard
      title="Data sources"
      caption="browser extension · Cato DEM every 15 min · identity daily — a stale source makes every panel above quiet, not healthy"
    >
      <div className="flex flex-col gap-6">
        <RumIntakeBlock />
        <CollectorBlock />
      </div>
    </PanelCard>
  );
}

/* ------------------------------------------------------------------------ *
 * The browser extension's intake
 * ------------------------------------------------------------------------ */

function RumIntakeBlock() {
  const status = useFoConsoleRumStatus();
  const data = status.data;
  const state = rumRolloutState(data);
  const copy = RUM_STATE_COPY[state];

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <SubHeading>Browser telemetry (fo-rum extension)</SubHeading>
        {data?.last_seen_at && (
          <Freshness label="Last post" since={data.last_seen_at} staleAfter={2 * 3_600_000} />
        )}
      </div>

      {status.error && (
        <Alert tone="danger" title="Couldn't read the browser-telemetry status">
          The NOVA API did not answer. This is a NOVA problem, not a browser one.
        </Alert>
      )}

      {status.isPending && !data ? (
        <Skeleton height="6rem" />
      ) : !data ? null : state === "unknown" ? (
        // The workspace did not answer: render the backend's own sentence and no
        // claim about the fleet — a failed query is not a fact about browsers.
        <div className="flex flex-col gap-2">
          <EmptyState size="sm" title={copy.title} description={data.detail} />
          <Typography variant="caption" tone="muted">
            Browsers reporting ÷ active F&O users: unknown (the activity query did not answer). Not
            zero, not &quot;not rolled out&quot;.
          </Typography>
        </div>
      ) : state !== "reporting" ? (
        <div className="flex flex-col gap-2">
          <EmptyState size="sm" title={copy.title} description={copy.detail} />
          <Typography variant="caption" tone="muted">
            Browsers reporting ÷ active F&O users: — (no browser has reported). Not 0 % of a working
            source.
          </Typography>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <RumCoverageMeter coverage={data.coverage} />
          <DescriptionList
            columns={4}
            items={[
              {
                label: "Browsers (24 h)",
                value: <span className="tabular-nums">{formatCount(data.users_24h)}</span>,
              },
              {
                label: "Rows accepted (24 h)",
                value: <span className="tabular-nums">{formatCount(data.records_24h)}</span>,
              },
              {
                label: "Last post",
                value: data.last_seen_at ? formatBinDateTime(data.last_seen_at) : "—",
              },
              { label: "Intake", value: data.enabled ? "enabled" : "disabled" },
            ]}
          />
          <Typography variant="caption" tone="muted">
            {copy.detail} A person outside this share has no browser block on their records, and the
            per-app user counts above are lower bounds on the fleet.
          </Typography>
        </div>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------------ *
 * The two NOVA collectors
 * ------------------------------------------------------------------------ */

function CollectorBlock() {
  const status = useFoConsoleCollectStatus();
  const run = useRunFoConsoleCollect();
  const [confirmTask, setConfirmTask] = useState<FoConsoleCollectTask | null>(null);

  const tasks = status.data?.tasks ?? [];

  return (
    <section className="flex flex-col gap-3">
      <SubHeading>Collectors</SubHeading>

      {status.error && (
        <Alert tone="danger" title="Couldn't read the collector status">
          The NOVA API did not answer. This is a NOVA problem, not a Cato or Entra one.
        </Alert>
      )}

      {status.isPending && !status.data ? (
        <Skeleton height="6rem" />
      ) : tasks.length === 0 ? (
        <EmptyState
          size="sm"
          title="No collector tasks registered"
          description="The backend reported no tasks. Until the DEM and identity jobs are registered in NOVA's poller, the console reads whatever the workbench prototypes last wrote."
        />
      ) : (
        <div className="flex flex-col gap-4">
          <StatusStrip aria-label="Collector tasks, current state">
            {tasks.map((task) => {
              const verdict = collectTaskVerdict(task);
              return (
                <StatusStrip.Cell
                  key={task.name}
                  status={verdict.status}
                  label={collectTaskLabel(task.name)}
                  title={`${collectTaskLabel(task.name)}: ${verdict.detail}`}
                  className="h-12"
                />
              );
            })}
          </StatusStrip>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {tasks.map((task) => {
              const verdict = collectTaskVerdict(task);
              const runnable = runnableName(task.name);
              return (
                <div key={task.name} className="flex flex-col gap-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <Typography variant="body-sm" className="font-medium">
                      {collectTaskLabel(task.name)}
                    </Typography>
                    {task.last_run_at && (
                      <Freshness
                        label="Last run"
                        since={task.last_run_at}
                        staleAfter={2 * collectTaskCadenceMs(task.name)}
                      />
                    )}
                  </div>
                  <DescriptionList
                    columns={2}
                    items={[
                      { label: "State", value: verdict.detail },
                      {
                        label: "Last window",
                        value: task.last_window_start
                          ? `from ${formatBinDateTime(task.last_window_start)}`
                          : "—",
                      },
                      {
                        label: "Rows last run",
                        value: (
                          <span className="tabular-nums">{formatCount(task.rows_last_run)}</span>
                        ),
                      },
                      {
                        label: "Configured / enabled",
                        value: `${task.configured ? "yes" : "no"} / ${task.enabled ? "yes" : "no"}`,
                      },
                    ]}
                  />
                  {task.error && (
                    <Alert tone="danger" title="Last run failed">
                      {task.error}
                    </Alert>
                  )}
                  {runnable && (
                    <div>
                      <Button
                        variant="outline"
                        tone="neutral"
                        size="sm"
                        startIcon={<Icon icon={Play} size="xs" />}
                        disabled={!task.configured || run.isPending}
                        loading={run.isPending && run.variables === runnable}
                        onClick={() => setConfirmTask(task)}
                      >
                        Run now
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {run.data && (
            <Alert
              tone={run.data.triggered ? "info" : "warning"}
              title={run.data.triggered ? "Run triggered" : "Run not triggered"}
            >
              {run.data.detail}
            </Alert>
          )}
          {run.error && (
            <Alert tone="danger" title="The run did not start">
              {run.error instanceof Error ? run.error.message : "The NOVA API refused the request."}
            </Alert>
          )}
          {status.data?.detail && (
            <Typography variant="caption" tone="muted">
              {status.data.detail}
            </Typography>
          )}
        </div>
      )}

      <AlertDialog.Root
        open={confirmTask != null}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmTask(null);
          }
        }}
      >
        <AlertDialog.Portal>
          <AlertDialog.Backdrop />
          <AlertDialog.Popup>
            <AlertDialog.Title>
              Run {confirmTask ? collectTaskLabel(confirmTask.name) : "the collector"} now?
            </AlertDialog.Title>
            <AlertDialog.Description>
              {confirmTask && runnableName(confirmTask.name) === "dem"
                ? "One appStats call against Cato for the last 15 minutes and one write to CatoDEM_CL. Counts against the 120 calls/min budget; harmless once, noisy if spammed."
                : "A full Graph pass over the aliases active in the last 30 days and a rewrite of FoUserMap_CL. Takes a minute or two; the identity join keeps working meanwhile."}
            </AlertDialog.Description>
            <div className="mt-4 flex justify-end gap-2">
              <AlertDialog.Close
                render={
                  <Button variant="ghost" tone="neutral">
                    Cancel
                  </Button>
                }
              />
              <Button
                onClick={() => {
                  const task = confirmTask ? runnableName(confirmTask.name) : null;
                  setConfirmTask(null);
                  if (task) {
                    run.mutate(task);
                  }
                }}
              >
                Run now
              </Button>
            </div>
          </AlertDialog.Popup>
        </AlertDialog.Portal>
      </AlertDialog.Root>
    </section>
  );
}

function SubHeading({ children }: { children: string }) {
  return (
    <Typography
      variant="caption"
      tone="muted"
      as="h3"
      className="font-semibold tracking-wide uppercase"
    >
      {children}
    </Typography>
  );
}
